import React, {useEffect, useMemo, useState} from 'react';
import ReactDOM from 'react-dom';
import {
  Badge,
  Box,
  Button,
  Fab,
  IconButton,
  InputBase,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {makeStyles, alpha, useTheme} from '@material-ui/core/styles';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import CodeIcon from '@material-ui/icons/Code';
import DeleteSweepIcon from '@material-ui/icons/DeleteSweep';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import PauseIcon from '@material-ui/icons/Pause';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import SearchIcon from '@material-ui/icons/Search';
import copyToClipboard from 'copy-to-clipboard';
import ReactJson from 'react-json-view';
import {FixedSizeList as List} from 'react-window';
import useDevPanelStore from '../../store/devPanelStore';
import {buildAgentPrompt, buildCodeSnippet, CODE_FORMATS} from './apiRequestFormats';

const MONO_FONT = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace';

/** Row geometry has to be fixed so the list can be virtualised. */
const ROW_GAP = 6;
const ROW_HEIGHT = 66;

const TABS = [
  {id: 'prompt', label: 'AI Prompt'},
  {id: 'code', label: 'Code'},
  {id: 'data', label: 'Raw data'},
];

const useStyles = makeStyles(theme => {
  const isDark = theme.palette.type === 'dark';
  // Neutral surfaces derived from the text colour so the panel reads the same
  // in both themes without introducing a second accent colour.
  const fill = opacity => alpha(theme.palette.text.primary, opacity);
  const surfaceSoft = fill(isDark ? 0.05 : 0.035);
  const surfaceStrong = fill(isDark ? 0.09 : 0.065);
  const borderSoft = fill(isDark ? 0.12 : 0.09);

  return {
    root: {
      position: 'fixed',
      left: theme.spacing(3),
      bottom: theme.spacing(3),
      zIndex: theme.zIndex.modal + 2,
      pointerEvents: 'none',
      '& > *': {
        pointerEvents: 'auto',
      },
    },
    fab: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
    panel: {
      width: 420,
      maxWidth: 'calc(100vw - 32px)',
      height: 700,
      maxHeight: 'calc(100vh - 32px)',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 12,
      overflow: 'hidden',
      position: 'absolute',
      left: 0,
      bottom: 0,
      border: `1px solid ${borderSoft}`,
      boxShadow: theme.shadows[10],
      backgroundColor: isDark
        ? alpha(theme.palette.background.default, 0.82)
        : alpha(theme.palette.background.paper, 0.82),
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      transition: theme.transitions.create('width', {
        duration: theme.transitions.duration.short,
      }),
    },
    panelExpanded: {
      width: 1000,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.25),
      padding: theme.spacing(1.25, 1.5),
      borderBottom: `1px solid ${borderSoft}`,
      flexShrink: 0,
    },
    headerIcon: {
      width: 30,
      height: 30,
      borderRadius: 6,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: surfaceStrong,
      color: theme.palette.text.secondary,
      flexShrink: 0,
    },
    headerText: {
      flex: 1,
      minWidth: 0,
    },
    headerTitle: {
      fontSize: 13,
      fontWeight: 600,
      lineHeight: 1.3,
    },
    headerSubtitle: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 11,
      color: theme.palette.text.secondary,
    },
    liveDot: {
      width: 6,
      height: 6,
      borderRadius: '50%',
      backgroundColor: theme.palette.secondary.main,
      flexShrink: 0,
    },
    liveDotPaused: {
      backgroundColor: theme.palette.warning.main,
    },
    headerActions: {
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      flexShrink: 0,
    },
    iconAction: {
      flexShrink: 0,
      padding: 6,
      color: theme.palette.text.secondary,
      '&:hover': {
        color: theme.palette.text.primary,
        backgroundColor: fill(0.08),
      },
    },
    split: {
      flex: 1,
      minHeight: 0,
      display: 'flex',
    },
    listColumn: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    listColumnNarrow: {
      flex: '0 0 330px',
      borderRight: `1px solid ${borderSoft}`,
      [theme.breakpoints.down('sm')]: {
        flex: '0 0 260px',
      },
    },
    detailColumn: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    search: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      margin: theme.spacing(1.25, 1.5, 0),
      padding: theme.spacing(0.5, 1.25),
      borderRadius: 8,
      backgroundColor: surfaceSoft,
      border: '1px solid transparent',
      flexShrink: 0,
      transition: 'border-color 0.16s ease',
      '&:focus-within': {
        borderColor: alpha(theme.palette.primary.main, 0.5),
      },
    },
    searchIcon: {
      color: theme.palette.text.secondary,
      fontSize: 17,
    },
    searchInput: {
      fontSize: 12,
    },
    listWrap: {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(1.25, 1.5, 1.5),
    },
    listMeasure: {
      flex: 1,
      minHeight: 0,
    },
    callRow: {
      height: ROW_HEIGHT - ROW_GAP,
      boxSizing: 'border-box',
      overflow: 'hidden',
      padding: theme.spacing(1, 1.25),
      marginBottom: ROW_GAP,
      borderRadius: 8,
      backgroundColor: 'transparent',
      cursor: 'pointer',
      transition: 'background-color 0.14s ease',
      '&:hover': {
        backgroundColor: surfaceSoft,
      },
    },
    callRowSelected: {
      backgroundColor: surfaceStrong,
      '&:hover': {
        backgroundColor: surfaceStrong,
      },
    },
    rowTop: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.75),
    },
    badge: {
      fontFamily: MONO_FONT,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: 0.4,
      padding: '2px 6px',
      borderRadius: 4,
      flexShrink: 0,
      lineHeight: 1.6,
    },
    timing: {
      flex: 1,
      textAlign: 'right',
      fontSize: 10,
      color: theme.palette.text.secondary,
      fontVariantNumeric: 'tabular-nums',
      whiteSpace: 'nowrap',
    },
    path: {
      marginTop: 3,
      fontFamily: MONO_FONT,
      fontSize: 12,
      color: theme.palette.text.primary,
    },
    subHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(1),
      padding: theme.spacing(1.25, 1, 1.25, 1.5),
      borderBottom: `1px solid ${borderSoft}`,
      flexShrink: 0,
    },
    subHeaderBody: {
      flex: 1,
      minWidth: 0,
    },
    subHeaderUrl: {
      marginTop: 5,
      fontFamily: MONO_FONT,
      fontSize: 11,
      color: theme.palette.text.secondary,
      wordBreak: 'break-all',
    },
    tabs: {
      minHeight: 34,
      borderBottom: `1px solid ${borderSoft}`,
      flexShrink: 0,
    },
    tab: {
      minHeight: 34,
      minWidth: 0,
      fontSize: 11.5,
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: 0.2,
    },
    tabPanel: {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(1.5),
    },
    scrollPanel: {
      flex: 1,
      minHeight: 0,
      overflow: 'auto',
      padding: theme.spacing(1.5),
    },
    block: {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 8,
      border: `1px solid ${borderSoft}`,
      backgroundColor: surfaceSoft,
      overflow: 'hidden',
    },
    blockToolbar: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      padding: theme.spacing(0.5, 0.5, 0.5, 0.75),
      borderBottom: `1px solid ${borderSoft}`,
      backgroundColor: surfaceStrong,
      flexShrink: 0,
    },
    langGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
    },
    langButton: {
      minWidth: 0,
      padding: '2px 8px',
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 600,
      lineHeight: 1.5,
      textTransform: 'none',
      color: theme.palette.text.secondary,
      '&:hover': {
        backgroundColor: fill(0.08),
      },
    },
    langButtonActive: {
      backgroundColor: fill(isDark ? 0.16 : 0.11),
      color: theme.palette.text.primary,
      '&:hover': {
        backgroundColor: fill(isDark ? 0.18 : 0.13),
      },
    },
    copyButton: {
      minWidth: 0,
      marginLeft: 'auto',
      padding: '2px 8px',
      borderRadius: 6,
      fontSize: 11,
      fontWeight: 600,
      lineHeight: 1.5,
      textTransform: 'none',
      whiteSpace: 'nowrap',
      color: theme.palette.text.secondary,
      '&:hover': {
        color: theme.palette.text.primary,
        backgroundColor: fill(0.08),
      },
    },
    copyButtonDone: {
      color: theme.palette.success.main,
      '&:hover': {
        color: theme.palette.success.main,
      },
    },
    codeBlock: {
      flex: 1,
      minHeight: 0,
      margin: 0,
      padding: theme.spacing(1.25),
      border: 'none',
      borderRadius: 0,
      backgroundColor: 'transparent',
      color: theme.palette.text.primary,
      fontFamily: MONO_FONT,
      fontSize: 11,
      lineHeight: 1.6,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      overflow: 'auto',
    },
    dataBlock: {
      marginBottom: theme.spacing(1.5),
    },
    dataLabel: {
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: 0.8,
      textTransform: 'uppercase',
      color: theme.palette.text.secondary,
      marginBottom: 5,
    },
    dataSurface: {
      borderRadius: 6,
      backgroundColor: surfaceSoft,
      padding: theme.spacing(1, 1.25),
      overflow: 'auto',
    },
    dataValue: {
      fontFamily: MONO_FONT,
      fontSize: 11,
      color: theme.palette.text.secondary,
      wordBreak: 'break-all',
    },
    emptyState: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      padding: theme.spacing(3),
    },
    emptyIcon: {
      width: 40,
      height: 40,
      borderRadius: 8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: theme.spacing(1.5),
      backgroundColor: surfaceStrong,
      color: theme.palette.text.secondary,
    },
    emptyTitle: {
      fontSize: 12.5,
      fontWeight: 600,
      marginBottom: 4,
    },
    emptyHint: {
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      maxWidth: 260,
      lineHeight: 1.5,
    },
  };
});

const formatTime = value => {
  try {
    return new Date(value).toLocaleTimeString();
  } catch {
    return value;
  }
};

const formatTiming = call =>
  [call.durationMs != null ? `${call.durationMs} ms` : null, formatTime(call.timestamp)]
    .filter(Boolean)
    .join(' · ');

const useMethodColor = () => {
  const theme = useTheme();
  return method => {
    switch ((method || 'GET').toUpperCase()) {
      case 'POST':
        return theme.palette.secondary.main;
      case 'PUT':
      case 'PATCH':
        return theme.palette.warning.main;
      case 'DELETE':
        return theme.palette.error.main;
      default:
        return theme.palette.primary.main;
    }
  };
};

/** Both badges use a tinted fill so neither shouts over the request path. */
const useBadgeStyle = () => {
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  return color => ({backgroundColor: alpha(color, isDark ? 0.22 : 0.14), color});
};

const MethodBadge = ({method, classes}) => {
  const methodColor = useMethodColor();
  const badgeStyle = useBadgeStyle();

  return (
    <span className={classes.badge} style={badgeStyle(methodColor(method))}>
      {method}
    </span>
  );
};

const StatusBadge = ({call, classes}) => {
  const theme = useTheme();
  const badgeStyle = useBadgeStyle();
  const color = call.pending
    ? theme.palette.warning.main
    : call.ok
      ? theme.palette.success.main
      : theme.palette.error.main;

  return (
    <span className={classes.badge} style={badgeStyle(color)}>
      {call.pending ? '···' : (call.status ?? 'failed')}
    </span>
  );
};

const CopyButton = ({text, label, classes}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!text) return;
    copyToClipboard(text);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <Button
      size="small"
      className={`${classes.copyButton} ${copied ? classes.copyButtonDone : ''}`}
      startIcon={
        copied ? (
          <CheckIcon style={{fontSize: 14}} />
        ) : (
          <FileCopyOutlinedIcon style={{fontSize: 14}} />
        )
      }
      onClick={handleCopy}
    >
      {copied ? 'Copied' : label}
    </Button>
  );
};

const PromptTab = ({call, classes}) => {
  const prompt = useMemo(() => buildAgentPrompt(call), [call]);

  return (
    <Box className={classes.tabPanel}>
      <Box className={classes.block}>
        <Box className={classes.blockToolbar}>
          <CopyButton text={prompt} label="Copy prompt" classes={classes} />
        </Box>
        <Box component="pre" className={classes.codeBlock}>
          {prompt}
        </Box>
      </Box>
    </Box>
  );
};

const CodeTab = ({call, classes}) => {
  const codeFormat = useDevPanelStore(state => state.codeFormat);
  const setCodeFormat = useDevPanelStore(state => state.setCodeFormat);

  const format = CODE_FORMATS.find(item => item.id === codeFormat) || CODE_FORMATS[0];
  const snippet = useMemo(() => buildCodeSnippet(call, format.id), [call, format.id]);

  return (
    <Box className={classes.tabPanel}>
      <Box className={classes.block}>
        <Box className={classes.blockToolbar}>
          <Box className={classes.langGroup}>
            {CODE_FORMATS.map(option => (
              <Button
                key={option.id}
                size="small"
                className={`${classes.langButton} ${
                  option.id === format.id ? classes.langButtonActive : ''
                }`}
                onClick={() => setCodeFormat(option.id)}
              >
                {option.label}
              </Button>
            ))}
          </Box>
          <CopyButton text={snippet} label="Copy" classes={classes} />
        </Box>
        <Box component="pre" className={classes.codeBlock}>
          {snippet}
        </Box>
      </Box>
    </Box>
  );
};

const DataBlock = ({label, children, classes}) => (
  <Box className={classes.dataBlock}>
    <Typography className={classes.dataLabel}>{label}</Typography>
    <Box className={classes.dataSurface}>{children}</Box>
  </Box>
);

const DataTab = ({call, classes, jsonTheme}) => {
  const jsonProps = {
    collapsed: 1,
    displayDataTypes: false,
    name: false,
    theme: jsonTheme,
    style: {backgroundColor: 'transparent', fontSize: 11},
  };

  return (
    <Box className={classes.scrollPanel}>
      <DataBlock label="URL" classes={classes}>
        <Typography className={classes.dataValue}>{call.url}</Typography>
      </DataBlock>
      {call.params != null && (
        <DataBlock label="Path parameters" classes={classes}>
          <Typography className={classes.dataValue}>{String(call.params)}</Typography>
        </DataBlock>
      )}
      {call.requestHeaders && (
        <DataBlock label="Request headers" classes={classes}>
          <ReactJson src={call.requestHeaders} {...jsonProps} />
        </DataBlock>
      )}
      {call.requestBody && (
        <DataBlock label="Request body" classes={classes}>
          <ReactJson src={call.requestBody} {...jsonProps} />
        </DataBlock>
      )}
      {call.error ? (
        <DataBlock label="Error" classes={classes}>
          <Typography variant="body2" color="error">
            {call.error}
          </Typography>
        </DataBlock>
      ) : (
        call.response != null && (
          <DataBlock label="Response" classes={classes}>
            <ReactJson src={call.response} {...jsonProps} collapsed={2} />
          </DataBlock>
        )
      )}
    </Box>
  );
};

const CallDetail = ({call, classes, jsonTheme, onClose}) => {
  const detailTab = useDevPanelStore(state => state.detailTab);
  const setDetailTab = useDevPanelStore(state => state.setDetailTab);

  return (
    <>
      <Box className={classes.subHeader}>
        <Box className={classes.subHeaderBody}>
          <Box className={classes.rowTop}>
            <MethodBadge method={call.method} classes={classes} />
            <StatusBadge call={call} classes={classes} />
            <span className={classes.timing}>{formatTiming(call)}</span>
          </Box>
          <Typography className={classes.subHeaderUrl}>{call.url}</Typography>
        </Box>
        <Tooltip arrow title="Close details">
          <span>
            <IconButton
              size="small"
              className={classes.iconAction}
              onClick={onClose}
              aria-label="Close details"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Tabs
        className={classes.tabs}
        value={detailTab}
        onChange={(_, value) => setDetailTab(value)}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
      >
        {TABS.map(tab => (
          <Tab key={tab.id} value={tab.id} label={tab.label} className={classes.tab} />
        ))}
      </Tabs>
      {detailTab === 'prompt' && <PromptTab call={call} classes={classes} />}
      {detailTab === 'code' && <CodeTab call={call} classes={classes} />}
      {detailTab === 'data' && <DataTab call={call} classes={classes} jsonTheme={jsonTheme} />}
    </>
  );
};

const CallRow = ({index, style, data}) => {
  const {calls, selectedCallId, onSelect, classes} = data;
  const call = calls[index];
  const isSelected = call.id === selectedCallId;

  return (
    <div style={style}>
      <Box
        className={`${classes.callRow} ${isSelected ? classes.callRowSelected : ''}`}
        onClick={() => onSelect(call.id)}
      >
        <Box className={classes.rowTop}>
          <MethodBadge method={call.method} classes={classes} />
          <StatusBadge call={call} classes={classes} />
          <span className={classes.timing}>{formatTiming(call)}</span>
        </Box>
        <Typography className={classes.path} noWrap>
          {call.path || call.url}
        </Typography>
      </Box>
    </div>
  );
};

/**
 * react-window needs a pixel height, but the column is sized by flexbox. A
 * callback ref is used because the measured node mounts conditionally.
 */
const useMeasuredHeight = () => {
  const [node, setNode] = useState(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!node) return undefined;

    const update = () => setHeight(node.clientHeight);
    update();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }

    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return [setNode, height];
};

const CallList = ({
  calls,
  totalCalls,
  paused,
  filter,
  setFilter,
  selectedCallId,
  onSelect,
  classes,
}) => {
  const [measureRef, listHeight] = useMeasuredHeight();

  const itemData = useMemo(
    () => ({calls, selectedCallId, onSelect, classes}),
    [calls, selectedCallId, onSelect, classes]
  );

  return (
    <>
      <Box className={classes.search}>
        <SearchIcon className={classes.searchIcon} />
        <InputBase
          className={classes.searchInput}
          placeholder="Filter by path, method or status"
          value={filter}
          onChange={event => setFilter(event.target.value)}
          fullWidth
        />
      </Box>
      <Box className={classes.listWrap}>
        {calls.length === 0 ? (
          <Box className={classes.emptyState}>
            <Box className={classes.emptyIcon}>
              <CodeIcon />
            </Box>
            <Typography className={classes.emptyTitle}>
              {totalCalls
                ? 'No matching requests'
                : paused
                  ? 'Recording paused'
                  : 'Waiting for requests'}
            </Typography>
            <Typography className={classes.emptyHint}>
              {totalCalls
                ? 'Try a different path, method or status code.'
                : paused
                  ? 'Resume recording to capture requests as you use the app.'
                  : 'Open a report or change a filter, and every request the app makes shows up here.'}
            </Typography>
          </Box>
        ) : (
          <Box className={classes.listMeasure} ref={measureRef}>
            {listHeight > 0 && (
              <List
                height={listHeight}
                width="100%"
                itemCount={calls.length}
                itemSize={ROW_HEIGHT}
                itemData={itemData}
              >
                {CallRow}
              </List>
            )}
          </Box>
        )}
      </Box>
    </>
  );
};

const DevPanel = () => {
  const classes = useStyles();
  const theme = useTheme();
  const open = useDevPanelStore(state => state.open);
  const paused = useDevPanelStore(state => state.paused);
  const calls = useDevPanelStore(state => state.calls);
  const selectedCallId = useDevPanelStore(state => state.selectedCallId);
  const toggleOpen = useDevPanelStore(state => state.toggleOpen);
  const setOpen = useDevPanelStore(state => state.setOpen);
  const setPaused = useDevPanelStore(state => state.setPaused);
  const clearCalls = useDevPanelStore(state => state.clearCalls);
  const setSelectedCallId = useDevPanelStore(state => state.setSelectedCallId);
  const [filter, setFilter] = useState('');

  const jsonTheme = theme.palette.type === 'dark' ? 'monokai' : 'rjv-default';

  const filteredCalls = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) return calls;
    return calls.filter(call =>
      [call.path, call.url, call.method, String(call.status), call.error]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query)
    );
  }, [calls, filter]);

  const selectedCall = filteredCalls.find(call => call.id === selectedCallId) || null;

  return ReactDOM.createPortal(
    <Box className={classes.root}>
      {open ? (
        <Paper
          className={`${classes.panel} ${selectedCall ? classes.panelExpanded : ''}`}
          elevation={0}
        >
          <Box className={classes.header}>
            <Box className={classes.headerIcon}>
              <CodeIcon fontSize="small" />
            </Box>
            <Box className={classes.headerText}>
              <Typography className={classes.headerTitle}>API inspector</Typography>
              <Typography className={classes.headerSubtitle} component="div">
                <span className={`${classes.liveDot} ${paused ? classes.liveDotPaused : ''}`} />
                {paused ? 'Paused' : 'Recording'} · {calls.length}{' '}
                {calls.length === 1 ? 'request' : 'requests'}
              </Typography>
            </Box>
            <Box className={classes.headerActions}>
              <Tooltip arrow title={paused ? 'Resume recording' : 'Pause recording'}>
                <span>
                  <IconButton
                    size="small"
                    className={classes.iconAction}
                    onClick={() => setPaused(!paused)}
                    aria-label={paused ? 'Resume recording' : 'Pause recording'}
                  >
                    {paused ? <PlayArrowIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip arrow title="Clear history">
                <span>
                  <IconButton
                    size="small"
                    className={classes.iconAction}
                    onClick={clearCalls}
                    aria-label="Clear history"
                  >
                    <DeleteSweepIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip arrow title="Hide panel">
                <span>
                  <IconButton
                    size="small"
                    className={classes.iconAction}
                    onClick={() => setOpen(false)}
                    aria-label="Hide panel"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          <Box className={classes.split}>
            <Box
              className={`${classes.listColumn} ${selectedCall ? classes.listColumnNarrow : ''}`}
            >
              <CallList
                calls={filteredCalls}
                totalCalls={calls.length}
                paused={paused}
                filter={filter}
                setFilter={setFilter}
                selectedCallId={selectedCallId}
                onSelect={setSelectedCallId}
                classes={classes}
              />
            </Box>
            {selectedCall && (
              <Box className={classes.detailColumn}>
                <CallDetail
                  key={selectedCall.id}
                  call={selectedCall}
                  classes={classes}
                  jsonTheme={jsonTheme}
                  onClose={() => setSelectedCallId(null)}
                />
              </Box>
            )}
          </Box>
        </Paper>
      ) : (
        <Tooltip arrow title="Open API inspector">
          <Badge badgeContent={calls.length} color="error" max={99} overlap="circle">
            <Fab size="medium" className={classes.fab} onClick={toggleOpen}>
              <CodeIcon />
            </Fab>
          </Badge>
        </Tooltip>
      )}
    </Box>,
    document.body
  );
};

export default DevPanel;
